import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Sparkles, MessageCircle } from 'lucide-react';
import { useContent, resolveAsset } from '../contexts/ContentContext';
import InquiryForm from '../components/InquiryForm';
import Footer from '../components/Footer';
import SEO, { productSchema } from '../components/SEO';
import RichDescription from '../components/RichDescription';

const formatPrice = (amount) => `₹ ${Number(amount).toLocaleString('en-IN')}`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products } = useContent();
  const [activeIdx, setActiveIdx] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  const product = useMemo(() => products.find((p) => p.id === id), [products, id]);
  const images = useMemo(() => {
    if (!product) return [];
    const arr = (product.images && product.images.length ? product.images : (product.image ? [product.image] : [])).filter(Boolean);
    return arr.map(resolveAsset);
  }, [product]);

  useEffect(() => {
    setActiveIdx(0);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  // Keyboard arrow navigation
  useEffect(() => {
    const onKey = (e) => {
      if (!images.length) return;
      if (e.key === 'ArrowLeft') setActiveIdx((i) => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setActiveIdx((i) => (i + 1) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length]);

  if (!product) {
    return (
      <section className="pdp-page" data-testid="product-detail-missing">
        <SEO title="Piece not found" path="/" noIndex />
        <div className="luxury-container" style={{ padding: '160px 0', textAlign: 'center' }}>
          <p className="body-large">This piece is no longer available.</p>
          <Link to="/archive" className="btn-secondary" style={{ marginTop: 24 }}>Return to Archive</Link>
        </div>
        <Footer />
      </section>
    );
  }

  const isReady = product.type === 'ready-to-wear' && product.price != null;
  const backHref = product.type === 'ready-to-wear' ? '/shop' : '/archive';
  const backLabel = product.type === 'ready-to-wear' ? 'Back to Shop' : 'Back to Collection';

  const orderViaWhatsApp = () => {
    const message = `Adaab. I would like to order "${product.name}"${product.price ? ` (${formatPrice(product.price)})` : ''} from the Silk & Sentiment collection. Please share availability and next steps.`;
    window.open(`https://wa.me/919151060995?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="pdp-page" data-testid={`product-detail-${product.id}`}>
      <SEO
        title={product.name}
        description={product.description}
        path={`/${product.type === 'archive' ? 'archive' : 'shop'}/${product.id}`}
        image={images[0] || undefined}
        type="product"
        schema={productSchema({ ...product, images })}
      />
      <div className="luxury-container">
        <button onClick={() => navigate(backHref)} className="pdp-back" data-testid="pdp-back">
          <ArrowLeft size={18} /> {backLabel}
        </button>

        <div className="pdp-grid">
          {/* Gallery */}
          <div className="pdp-gallery">
            <div className="pdp-main-image" data-testid="pdp-main-image">
              {images.length > 0 ? (
                <img src={images[activeIdx]} alt={`${product.name} ${activeIdx + 1}`} />
              ) : (
                <div className="pdp-image-placeholder">No image</div>
              )}
              {images.length > 1 && (
                <>
                  <button
                    className="pdp-nav pdp-nav-prev"
                    onClick={() => setActiveIdx((i) => (i - 1 + images.length) % images.length)}
                    aria-label="Previous image"
                    data-testid="pdp-prev"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    className="pdp-nav pdp-nav-next"
                    onClick={() => setActiveIdx((i) => (i + 1) % images.length)}
                    aria-label="Next image"
                    data-testid="pdp-next"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <div className="pdp-counter">{activeIdx + 1} / {images.length}</div>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="pdp-thumbs" data-testid="pdp-thumbs">
                {images.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    className={`pdp-thumb ${i === activeIdx ? 'active' : ''}`}
                    onClick={() => setActiveIdx(i)}
                    aria-label={`View image ${i + 1}`}
                    data-testid={`pdp-thumb-${i}`}
                  >
                    <img src={src} alt={`thumb ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="pdp-details">
            <p className="navigation-text" style={{ color: 'var(--accent-gold)' }}>
              {product.type === 'archive'
                ? 'Heritage Couture'
                : product.type === 'suit'
                  ? 'The Suit Atelier'
                  : 'Ready to Wear'}
            </p>
            <h1 className="hero-large" style={{ marginTop: 8, marginBottom: 16 }}>{product.name}</h1>

            {isReady && (
              <p className="pdp-price" data-testid="pdp-price">{formatPrice(product.price)}</p>
            )}

            {product.status && product.status !== 'Available' && (
              <span className={`pdp-status pdp-status-${product.status.replace(/[^a-z]/gi, '').toLowerCase()}`}>
                <Sparkles size={12} /> {product.status}
              </span>
            )}

            <RichDescription text={product.description} className="pdp-description" testId="pdp-description" />

            {product.disclaimer && (
              <aside className="pdp-disclaimer" data-testid="pdp-product-disclaimer">
                <p className="pdp-disclaimer-label">A note from the atelier</p>
                <p className="pdp-disclaimer-text">{product.disclaimer}</p>
              </aside>
            )}

            <dl className="pdp-meta">
              {product.fabric && (
                <div className="pdp-meta-row">
                  <dt>Fabric</dt>
                  <dd>{product.fabric}</dd>
                </div>
              )}
              {product.embroidery && (
                <div className="pdp-meta-row">
                  <dt>Embroidery / Technique</dt>
                  <dd>{product.embroidery}</dd>
                </div>
              )}
              {product.origin && (
                <div className="pdp-meta-row">
                  <dt>Origin</dt>
                  <dd>{product.origin}</dd>
                </div>
              )}
              <div className="pdp-meta-row">
                <dt>Crafting</dt>
                <dd>Hand-finished by master karigars in Lucknow</dd>
              </div>
            </dl>

            <div className="pdp-cta-row">
              {isReady ? (
                <button className="btn-primary" onClick={orderViaWhatsApp} data-testid="pdp-order-btn">
                  <MessageCircle size={14} /> Order via WhatsApp
                </button>
              ) : (
                <button className="btn-primary" onClick={() => setInquiryOpen(true)} data-testid="pdp-inquire-btn">
                  Inquire Now
                </button>
              )}
              <Link to={backHref} className="btn-secondary">{backLabel}</Link>
            </div>
          </div>
        </div>
      </div>

      <InquiryForm
        isOpen={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        selectedPiece={{ id: product.id, name: product.name }}
      />

      <Footer />
    </section>
  );
};

export default ProductDetailPage;
